import React, { useState } from "react";
import useAuth from "../hooks/useAuth";
import { resolveBackendUrl } from "../utils/accountAvatarUrl";
import { useClientSignoffStatsContext } from "./ClientSignoffStatsContext";
import { clientInitials } from "./clientPortalSignoffNav";
import ClientPortalSignoffModal from "./ClientPortalSignoffModal";

function ClientAvatar({ row }) {
  const [failed, setFailed] = React.useState(false);
  const src = resolveBackendUrl(row.avatarUrl);
  const initials = clientInitials(row.name);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        className="w-8 h-8 rounded-full object-cover flex-shrink-0"
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <div className="w-8 h-8 rounded-full bg-violet-600 text-white text-xs font-semibold flex items-center justify-center flex-shrink-0">
      {initials}
    </div>
  );
}

function hasReviewPermission(row) {
  return row.capabilities?.canManageTimesheetSignoff === true;
}

export default function ClientDashboardPendingSignoff() {
  const auth = useAuth();
  const signoff = useClientSignoffStatsContext();
  const accountId = auth?.accountId || localStorage.getItem("accountId");
  const [activeClient, setActiveClient] = useState(null);

  if (!signoff.showPanel) {
    return null;
  }

  const showReviewColumn = signoff.anyTimesheetReviewRequired;
  const showInitialLoading = signoff.loading && !signoff.initialized;

  return (
    <>
      <div className="w-full min-w-0 flex flex-col">
        <h3 className="text-center text-lg font-semibold text-gray-800 mb-4">
          Shifts pending sign-off
        </h3>

        {showInitialLoading ? (
          <p className="text-sm text-muted-foreground text-center py-6">
            Loading sign-off data...
          </p>
        ) : (
          <div className="border border-gray-200 rounded-lg bg-white shadow-sm flex-1 flex flex-col">
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-xs uppercase tracking-wide text-gray-500 font-semibold">
                    <th className="px-4 py-2.5 text-left">Client</th>
                    <th className="px-4 py-2.5 text-center whitespace-nowrap">
                      Pending sign-off
                    </th>
                    {showReviewColumn ? (
                      <th className="px-4 py-2.5 text-center whitespace-nowrap">
                        Pending review
                      </th>
                    ) : null}
                    <th className="px-4 py-2.5 text-right whitespace-nowrap min-w-[6.5rem]">
                      &nbsp;
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {signoff.clientShiftCounts.length === 0 ? (
                    <tr>
                      <td
                        colSpan={showReviewColumn ? 4 : 3}
                        className="px-4 py-6 text-center text-sm text-muted-foreground"
                      >
                        No shifts pending sign-off
                      </td>
                    </tr>
                  ) : (
                    signoff.clientShiftCounts.map((row) => (
                      <tr key={row.clientId} className="hover:bg-gray-50">
                        <td className="px-4 py-3 min-w-[10rem]">
                          <div className="flex items-center gap-2 min-w-0">
                            <ClientAvatar row={row} />
                            <span className="font-medium text-gray-800">
                              {row.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center text-gray-700 whitespace-nowrap">
                          {row.pendingSignoffCount} Shift
                          {row.pendingSignoffCount === 1 ? "" : "s"}
                        </td>
                        {showReviewColumn ? (
                          <td className="px-4 py-3 text-center text-gray-700 whitespace-nowrap">
                            {row.timesheetReviewRequired && hasReviewPermission(row)
                              ? `${row.pendingReviewCount} Shift${row.pendingReviewCount === 1 ? "" : "s"}`
                              : ""}
                          </td>
                        ) : null}
                        <td className="px-4 py-3 text-right whitespace-nowrap">
                          <button
                            type="button"
                            onClick={() => setActiveClient(row)}
                            className="inline-flex items-center justify-center bg-red-600 hover:bg-red-700 text-white text-xs font-bold uppercase tracking-wide px-4 py-2 rounded shadow-sm whitespace-nowrap"
                          >
                            Sign Off
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <ClientPortalSignoffModal
        open={!!activeClient}
        clientRow={activeClient}
        accountId={accountId}
        onClose={() => setActiveClient(null)}
        onComplete={() => {
          signoff.refresh();
          setActiveClient(null);
        }}
      />
    </>
  );
}
